/**
 * One open book.
 *
 * The shell owns nothing but layout and the panels. Position, audio and paging
 * all live in useReader, so this file never has to decide what "where am I" means.
 *
 * Everything here degrades. No audio: no transport, the text still reads. No
 * alignment: no highlight, clicking does nothing, the text still reads. No PDF:
 * the page toggle is disabled and says why. A book is worth opening even when
 * three quarters of the pipeline has not run yet.
 */

import { Suspense, lazy, useCallback, useMemo, useRef, useState } from "react";

import {
  LibraryIcon,
  PageIcon,
  SettingsIcon,
  TextIcon,
} from "../components/icons";
import { IconButton } from "../components/ui";
import { t, plural, useUiLang } from "../i18n";
import { api } from "../lib/api";
import { navigate } from "../lib/route";
import { useReader } from "../lib/useReader";
import { BookmarkPanel } from "./BookmarkPanel";
import { ChapterNav } from "./ChapterNav";
import { EditionSwitch } from "./EditionSwitch";
import { PlayerBar } from "./PlayerBar";
import { OfflineButton } from "./OfflineButton";
import {
  ReadingPrefsPanel,
  readingVars,
  useReadingPrefs,
} from "./ReadingPrefs";
import { TextView } from "./TextView";
import { SHORTCUTS, useKeyboard } from "./useKeyboard";

// pdf.js is a megabyte of worker and viewer code. A reader who never opens the
// page view should never pay for it, so it arrives on the first toggle.
const PageView = lazy(() =>
  import("./PageView").then((module) => ({ default: module.PageView })),
);

function Skeleton() {
  // A shape, not a spinner: the page that is coming is already drawn, so nothing
  // jumps when the text lands.
  const widths = ["92%", "97%", "88%", "95%", "72%", "96%", "90%", "45%"];
  return (
    <div className="mx-auto max-w-[var(--measure)] px-6 pt-[14vh]" aria-hidden="true">
      <div className="mb-8 h-3 w-24 animate-pulse rounded bg-[var(--color-surface)]" />
      {widths.map((width, at) => (
        <div
          key={at}
          className="mb-4 h-4 animate-pulse rounded bg-[var(--color-surface)]"
          style={{ width, animationDelay: `${at * 60}ms` }}
        />
      ))}
    </div>
  );
}

function PageFrame() {
  return (
    <div className="flex h-full items-center justify-center" aria-hidden="true">
      <div className="h-[70%] w-[46%] animate-pulse rounded-sm bg-[var(--color-surface)]" />
    </div>
  );
}

function Help({ onClose }: { onClose: () => void }) {
  useUiLang();
  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={t("readerShortcutsDialog")}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-xl bg-[var(--color-raised)] p-5 shadow-2xl shadow-black/60 ring-1 ring-[var(--color-line)]"
      >
        <h2 className="mb-4 text-sm font-medium">{t("readerKeyboard")}</h2>
        <dl className="grid gap-2">
          {SHORTCUTS.map((shortcut) => (
            <div
              key={shortcut.keys}
              className="flex items-baseline justify-between gap-6"
            >
              <dt className="font-mono text-xs text-[var(--color-accent)]">
                {shortcut.keys}
              </dt>
              <dd className="text-xs text-[var(--color-muted)]">
                {t(shortcut.what)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export function ReaderScreen({ bookId }: { bookId: string }) {
  const { state, view, setView, player, goToSentence, goToChapter, sleep, audioPrefs } =
    useReader(bookId);
  const { prefs, update, reset } = useReadingPrefs();
  useUiLang();

  const [chapters, setChapters] = useState(false);
  const [marks, setMarks] = useState(false);
  const [settings, setSettings] = useState(false);
  const [help, setHelp] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  const { book, chapter, sentences, focus, player: audio } = state;
  const canPage = book?.has_doc === true;
  const interactive = book?.has_sync === true && sentences.length > 0;

  const step = useCallback(
    (direction: 1 | -1) => {
      const next = Math.min(
        Math.max(0, focus + direction),
        sentences.length - 1,
      );
      if (sentences[next]) goToSentence(next);
    },
    [focus, goToSentence, sentences],
  );

  const openSearch = useCallback(() => {
    setChapters(true);
    // After the panel is in the tree, or the field is not there to focus yet.
    window.setTimeout(() => searchRef.current?.focus(), 60);
  }, []);

  useKeyboard({
    toggle: () => book?.has_audio && player.toggle(),
    nudge: (seconds) => player.nudge(seconds),
    step,
    rate: (direction) => player.cycleRate(direction),
    toggleView: () => canPage && setView(view === "text" ? "page" : "text"),
    toggleChapters: () => setChapters((open) => !open),
    focusSearch: openSearch,
    toggleHelp: () => setHelp((open) => !open),
    escape: () => {
      setChapters(false);
      setSettings(false);
      setHelp(false);
      if (document.activeElement instanceof HTMLElement)
        document.activeElement.blur();
    },
  });

  // The text of the sentence being spoken, for the page view to try to find.
  const spoken = useMemo(() => {
    const sentence = sentences[focus];
    if (!chapter || !sentence || sentence.chap !== chapter.index) return "";
    return chapter.text.slice(sentence.cs, sentence.ce);
  }, [chapter, focus, sentences]);

  const onPageCount = useCallback((count: number) => setPageCount(count), []);

  if (state.error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm text-[var(--color-ink)]">
          {t("readerOpenFailed")}
        </p>
        <p className="max-w-sm text-xs text-[var(--color-muted)]">
          {state.error}
        </p>
        <button
          onClick={() => navigate({ name: "library" })}
          className="mt-2 text-xs text-[var(--color-accent)] hover:underline"
        >
          {t("readerBackToLibrary")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" style={readingVars(prefs)}>
      <header className="relative z-30 flex shrink-0 items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-3 short:py-1.5">
        <IconButton
          label={t("readerBackToLibrary")}
          onClick={() => navigate({ name: "library" })}
        >
          <LibraryIcon />
        </IconButton>

        <div className="min-w-0 flex-1">
          {book ? (
            <>
              <h1 className="truncate text-sm font-medium leading-tight text-[var(--color-ink)]">
                {book.title}
              </h1>
              {/* Below 400px the header has five things to hold and the author
                  is the one nobody came for. Truncated to "Robert B. ..." it
                  says less than the empty line it occupies, and the title above
                  it is left cut short to make room. Gone under 400, whole above. */}
              <p
                className={
                  "truncate text-xs leading-tight text-[var(--color-faint)] " +
                  (book.editions.length > 1 ? "max-[399px]:hidden" : "")
                }
              >
                {book.author}
                {view === "page" &&
                  pageCount > 0 &&
                  ` · ${plural(pageCount, "readerPageOne", "readerPageMany")}`}
              </p>
            </>
          ) : (
            <div className="h-4 w-40 animate-pulse rounded bg-[var(--color-surface)]" />
          )}
        </div>

        {book && book.editions.length > 1 && (
          <EditionSwitch
            editions={book.editions}
            current={book.id}
            fraction={
              // Where the reader is, as a share of the book. The sentence is
              // the only measure the two editions have in common: minutes of
              // narration and page numbers do not survive a translation.
              focus >= 0 && book.sentence_count > 0
                ? focus / book.sentence_count
                : null
            }
          />
        )}

        {/* Before the view switch, not after: downloading a book is about the
            book, and the two controls after it are about how it is displayed. */}
        {book && <OfflineButton book={book} />}

        <div
          role="group"
          aria-label={t("readerViewGroup")}
          className="flex items-center gap-0.5 rounded-lg bg-[var(--color-surface)] p-0.5"
        >
          {(
            [
              ["text", "readerViewText", TextIcon],
              ["page", "readerViewPage", PageIcon],
            ] as const
          ).map(([mode, labelKey, Icon]) => (
            <button
              key={mode}
              onClick={() => setView(mode)}
              disabled={mode === "page" && !canPage}
              aria-pressed={view === mode}
              aria-label={t(labelKey)}
              title={
                mode === "page" && !canPage
                  ? t("readerNoDocument")
                  : t(labelKey)
              }
              className={
                "tap flex h-8 w-9 items-center justify-center rounded-md transition-colors duration-150 " +
                "disabled:opacity-30 " +
                (view === mode
                  ? "bg-[var(--color-raised)] text-[var(--color-accent)]"
                  : "text-[var(--color-muted)] hover:text-[var(--color-ink)]")
              }
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>

        <div className="relative">
          <IconButton
            label={t("readerPreferences")}
            onClick={() => setSettings((open) => !open)}
            aria-expanded={settings}
          >
            <SettingsIcon />
          </IconButton>
          {settings && (
            <ReadingPrefsPanel
              prefs={prefs}
              onChange={update}
              onReset={reset}
              onClose={() => setSettings(false)}
            />
          )}
        </div>
      </header>

      <main className="relative min-h-0 flex-1 overflow-hidden">
        {state.loading || !book ? (
          <Skeleton />
        ) : view === "page" ? (
          <Suspense fallback={<PageFrame />}>
            <PageView
              docUrl={api.docUrl(bookId, book.doc_rev)}
              hasDoc={book.has_doc}
              page={state.page}
              sentences={sentences}
              pagemap={state.pagemap}
              spoken={spoken}
              focus={focus}
              onSelect={goToSentence}
              onPageCount={onPageCount}
            />
          </Suspense>
        ) : chapter ? (
          <TextView
            text={chapter.text}
            sentences={chapter.sentences}
            focus={focus}
            playing={audio.playing}
            interactive={interactive}
            font={prefs.font}
            chapterIndex={state.chapterIndex}
            onSelect={goToSentence}
            onTurnChapter={(direction) =>
              goToChapter(state.chapterIndex + direction)
            }
          />
        ) : (
          <Skeleton />
        )}

        {book && (
          <ChapterNav
            open={chapters}
            book={book}
            pagemap={state.pagemap}
            chapterIndex={state.chapterIndex}
            searchRef={searchRef}
            onClose={() => setChapters(false)}
            onGoToChapter={goToChapter}
            onGoToSentence={goToSentence}
          />
        )}

        {book && (
          <BookmarkPanel
            open={marks}
            onClose={() => setMarks(false)}
            bookId={bookId}
            currentTime={audio.time}
            onSeek={(at) => {
              player.seek(at);
              setMarks(false);
            }}
          />
        )}

        {help && <Help onClose={() => setHelp(false)} />}
      </main>

      {book?.has_audio ? (
        <PlayerBar
          player={player}
          state={audio}
          hasSync={book.has_sync}
          chapterIndex={state.chapterIndex}
          chapterCount={book.chapters}
          onOpenChapters={() => setChapters(true)}
          onOpenBookmarks={() => setMarks(true)}
          sleep={sleep}
          audioPrefs={audioPrefs}
        />
      ) : (
        <footer className="flex shrink-0 items-center justify-center gap-2 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] text-xs text-[var(--color-muted)]">
          <button
            onClick={() => goToChapter(state.chapterIndex - 1)}
            disabled={state.chapterIndex <= 0}
            className="tap rounded-lg px-3 py-1.5 transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)] disabled:opacity-30"
          >
            {t("readerPrevious")}
          </button>
          <button
            onClick={() => setChapters(true)}
            className="tap rounded-lg px-3 py-1.5 transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]"
          >
            {t("readerChapter", { n: state.chapterIndex + 1 })}
            <span className="text-[var(--color-faint)]">
              {" "}
              {t("readerChapterOf", { total: book?.chapters ?? 1 })}
            </span>
          </button>
          <button
            onClick={() => goToChapter(state.chapterIndex + 1)}
            disabled={!book || state.chapterIndex >= book.chapters - 1}
            className="tap rounded-lg px-3 py-1.5 transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)] disabled:opacity-30"
          >
            {t("readerNext")}
          </button>
        </footer>
      )}
    </div>
  );
}
